import { Box, Button, IconButton, Typography } from "@mui/material";
import FsLightbox from "fslightbox-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector } from "../../app/hooks";
import { City } from "../../constants/const";
import { GarageActions } from "../../constants/enum/garage";
import { CarInventoryType } from "../../types/CarInventoryType";
import { toUSDFormat } from "../../utils/utils";

import CustomDropDown from "../CustomDropdown/CustomDropDown";
import GarageRepairPopup from "../GaragePopup/GarageRepairPopup";
import GarageSellPopup from "../GaragePopup/GarageSellPopup";
import GarageShipPopup from "../GaragePopup/GarageShipPopup";
import GarageTransferPopup from "../GaragePopup/GarageTransferPopup";

import useStyles from "./index.styles";

const GarageList = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [actionType, setActionType] = useState<GarageActions | "">("");
  const [openShipPopup, setOpenShipPopup] = useState(false);
  const [openTransferPopup, setOpenTransferPopup] = useState(false);
  const [openSellPopup, setOpenSellPopup] = useState(false);
  const [openRepairPopup, setOpenRepairPopup] = useState(false);
  const [targetItem, setTargetItem] = useState<CarInventoryType | null>(null);
  const [showImage, setShowImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // const [selectedCars, setSelectedCars] = useState<number[]>([]); // State for selected cars

  const { myGarageList } = useAppSelector((state) => state.game);

  // const handleSelectAll = (checked: boolean) => {
  //   if (checked) {
  //     setSelectedCars(myGarageList.map((car) => car.id));
  //   } else {
  //     setSelectedCars([]);
  //   }
  // };

  // const handleSelectCar = (carId: number, checked: boolean) => {
  //   setSelectedCars((prev) =>
  //     checked ? [...prev, carId] : prev.filter((id) => id !== carId)
  //   );
  // };

  const handleClickSell = (item: CarInventoryType) => {
    setOpenSellPopup(true);
    setTargetItem(item);
  };

  const handleClickRepair = (item: CarInventoryType) => {
    setOpenRepairPopup(true);
    setTargetItem(item);
  };

  const handleChangeActionType = (
    actionType: GarageActions,
    item: CarInventoryType
  ) => {
    setTargetItem(item);

    if (actionType === GarageActions.ListOnMarket) {
      navigate(`/market?listItem=${item?.id}`);
      return;
    }

    if (actionType === GarageActions.ShipCar) {
      setOpenShipPopup(true);
      return;
    }

    if (actionType === GarageActions.Transfer) {
      setOpenTransferPopup(true);
      return;
    }
  };

  const sortedGarageList = useMemo(
    () =>
      [...myGarageList].sort((car1, car2) => car2.timestamp - car1.timestamp),
    [myGarageList]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (parseInt(queryParams.get("sellItem") || "0") > 0) {
      setOpenSellPopup(true);
      const sellItem = myGarageList.find(
        (item) => item.id === parseInt(queryParams.get("sellItem") || "0")
      );
      if (sellItem) setTargetItem(sellItem);
    }
  }, [location, myGarageList]);

  return (
    <Box className={classes.prisonerListPanel}>
      <Box className={classes.prisonerListHeader}>
        {/* <Checkbox
          checked={selectedCars.length === myGarageList.length}
          indeterminate={
            selectedCars.length > 0 && selectedCars.length < myGarageList.length
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        /> */}
        <Typography>Car</Typography>
        <Typography>Location</Typography>
        <Typography>Action</Typography>
        <Typography>Damage</Typography>
        <Typography>Value</Typography>
        <Typography>Repair</Typography>
        <Typography>Sell</Typography>
      </Box>

      {sortedGarageList.map((item: CarInventoryType, index) => (
        <Box className={classes.carListItem} key={index}>
          {/* <Checkbox
            checked={selectedCars.includes(item.id)}
            onChange={(e) => handleSelectCar(item.id, e.target.checked)}
          /> */}
          <Box className={classes.carItem}>
            <Box
              component={"img"}
              src={`/assets/imgs/cars/${item.carType.image}.png`}
              alt="Car"
              onClick={() => {
                setShowImage(!showImage);
                setCurrentImageIndex(index);
              }}
            />
            <Typography>{item.carType.carName}</Typography>
          </Box>
          <Typography>{City[item.city]}</Typography>
          <CustomDropDown
            items={Object.values(GarageActions)}
            placeholder="Select"
            className={classes.citySelect}
            handleChange={(e) => {
              handleChangeActionType(e.target.value as GarageActions, item);
            }}
            value={actionType}
          />
          <Typography>{item.car.damagePercent} %</Typography>
          <Box className={classes.carValue}>
            <Box
              src="/assets/imgs/layout/currency.png"
              alt="Currency"
              component="img"
              sx={{
                width: 24,
                height: 24,
              }}
            />
            <Typography>$ {toUSDFormat(item.car.value)}</Typography>
          </Box>

          <IconButton
            className={classes.repairButton}
            onClick={() => handleClickRepair(item)}
            disabled={item.car.damagePercent === 0}
          >
            <img src="/assets/imgs/garage/repair.png" alt="Repair" />
          </IconButton>

          <Button
            variant="contained"
            className={classes.sellButton}
            onClick={() => handleClickSell(item)}
          >
            Sell
          </Button>
        </Box>
      ))}

      <GarageShipPopup
        openModal={openShipPopup}
        setOpenModal={setOpenShipPopup}
        setActionType={setActionType}
        targetItem={targetItem}
      />

      <GarageTransferPopup
        openModal={openTransferPopup}
        setOpenModal={setOpenTransferPopup}
        setActionType={setActionType}
        targetItem={targetItem}
      />

      <GarageSellPopup
        openModal={openSellPopup}
        setOpenModal={setOpenSellPopup}
        targetItem={targetItem}
      />

      <GarageRepairPopup
        openModal={openRepairPopup}
        setOpenModal={setOpenRepairPopup}
        targetItem={targetItem}
      />

      <FsLightbox
        toggler={showImage}
        sources={sortedGarageList.map((item: CarInventoryType) => (
          <div>
            <img
              src={`/assets/imgs/cars/${item.carType.image}.png`}
              alt={item.carType.carName}
            ></img>

            <Typography className={classes.galleryCarName}>
              {item.carType.carName}
            </Typography>
          </div>
        ))}
        slide={currentImageIndex + 1}
        captions={sortedGarageList.map(
          (item: CarInventoryType) => item.carType.carName
        )}
      />
    </Box>
  );
};

export default GarageList;
